import React from 'react';
import { css, cx } from '@linaria/atomic';
import { AiContainedIcon } from '@frontend/styled-icons';
import { theme } from '@frontend/theme';
import { NakedButton, Truncated, Text } from '@frontend/design-system';

export interface AIRButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  trackingId?: string;
  iconOnly?: boolean;
}

export const AIRButton = ({ children = 'AI Receptionist', onClick, trackingId, iconOnly, ...rest }: AIRButtonProps) => {
  return (
    <NakedButton {...rest} trackingId={trackingId} className={styles.button} onClick={onClick}>
      <div className={cx('button-content', styles.buttonContent)}>
        <div className={cx(styles.iconContainer, !iconOnly && styles.iconBordered)}>
          <AiContainedIcon className='ai-icon' containerStyles={styles.aiIcon} iconName='aiweave-assistant' size={40} />
        </div>
        {!iconOnly && (
          <div
            style={{
              padding: '0 var(--fabric-space-padding-sm)',
              fontWeight: 'var(--fabric-font-weight-bold)',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {typeof children === 'string' ? (
              <Truncated as={Text} weight='bold'>
                {children}
              </Truncated>
            ) : (
              children
            )}
          </div>
        )}
      </div>
    </NakedButton>
  );
};

const styles = {
  button: css`
    background: linear-gradient(var(--fabric-color-surface-default), var(--fabric-color-surface-default)) padding-box,
      linear-gradient(90deg, #1950a3 0%, #0d5d59 100%) border-box;
    border: 1px solid transparent;
    border-radius: var(--fabric-radius-sm);
    box-shadow: 0px 1px 1px 0px var(--fabric-color-shadow-light-base) 0px 1px 3px 1px
      var(--fabric-color-shadow-light-base);
    position: relative;
    overflow: hidden;
    max-width: 100%;
    min-width: 0;

    /* This is precise because of how we position the AiContainedIcon */
    height: 42px;

    &:hover .button-content,
    &:focus .button-content,
    &:focus-within .button-content {
      background: linear-gradient(
        -90deg,
        ${theme.colors.primary10},
        ${theme.colors.secondary.seaweed5},
        ${theme.colors.primary10},
        ${theme.colors.secondary.seaweed5}
      );
      background-size: 400% 400%;
      animation: air-button-gradient 5s ease infinite;
    }

    &:hover .ai-icon,
    &:focus .ai-icon,
    &:focus-within .ai-icon {
      background: linear-gradient(-90deg, #1950a3, #0d5d59, #1950a3, #0d5d59);
      background-size: 400% 400%;
      animation: air-button-gradient 5s ease infinite;
      color: white;
    }

    @keyframes air-button-gradient {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
  `,
  buttonContent: css`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    min-width: 0;
    overflow: hidden;

    /* This nests the rounded corners of the border better */
    border-radius: 3px;
    height: 40px;
  `,
  iconContainer: css`
    background: linear-gradient(var(--fabric-color-surface-default), var(--fabric-color-surface-default)) padding-box,
      linear-gradient(90deg, #1950a3 0%, #0d5d59 100%) border-box;
  `,
  iconBordered: css`
    display: grid;
    border: 1px solid transparent;
    /* This nests the rounded corners of the border better */
    border-radius: 3px;
    position: relative;
    top: -1px;
    left: -1px;
  `,
  aiIcon: css`
    border-radius: 2px;
  `,
};
